import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchProducts } from "@/lib/products";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { byModel, pauseScroll, scrollToId } from "@/lib/store";
import { searchProducts, guessCategory } from "@/lib/search";
import { SearchShortcuts, SearchResults, SearchEmpty } from "./SearchParts";
import { EASE } from "./Reveal";

const FIXED = [
  { key: "novos", label: "Novos lacrados", link: { id: "estoque", cond: "novo" } },
  { key: "seminovos", label: "Seminovos", link: { id: "estoque", cond: "seminovo" } },
  { key: "acessorios", label: "Acessórios HPrime", link: { id: "estoque", cond: "acessorios" } },
  { key: "pedido", label: "Fazer pedido", link: { id: "pedido" } },
];

const stockModels = (products) => [...new Set(products.filter((p) => (p.category || "iphone") === "iphone").sort(byModel).map((p) => p.model))].slice(0, 3);

const useOrder = (close) => {
  const navigate = useNavigate();
  const onHome = useLocation().pathname === "/";
  return (query) => {
    const detail = { model: query, category: guessCategory(query) };
    close();
    if (!onHome) {
      navigate(`/?cat=${detail.category}&model=${encodeURIComponent(query)}#pedido`);
      return;
    }
    window.dispatchEvent(new CustomEvent("rc-order", { detail }));
    scrollToId("pedido");
  };
};

const Panel = ({ onClose, go }) => {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const input = useRef(null);
  const q = query.trim();
  const list = useMemo(() => searchProducts(products, q), [products, q]);
  const logged = useRef("");
  const report = (action) => {
    if (q.length < 2 || (logged.current === q && action === "search")) return;
    if (logged.current !== q) track("search", { term: q.toLowerCase().slice(0, 60) });
    logged.current = q;
    api.post("/search/log", { query: q.slice(0, 60), results: list.length, action }).catch(() => null);
  };
  const finish = (action = "search") => {
    report(action);
    pauseScroll(false);
    onClose();
  };
  const close = () => finish();
  const closeRef = useRef(close);
  closeRef.current = close;
  const order = useOrder(() => finish("order"));

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
    pauseScroll(true);
    const t = setTimeout(() => input.current?.focus(), 80);
    const onKey = (e) => { if (e.key === "Escape") closeRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); pauseScroll(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shortcuts = [
    ...stockModels(products).map((m) => ({ key: m.toLowerCase().replace(/\s+/g, "-"), label: m, onClick: () => setQuery(m) })),
    ...FIXED.map((f) => ({ ...f, onClick: () => { close(); go(f.link); } })),
  ];

  return (
    <motion.div className="fixed inset-0 z-[70]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
      <button data-testid="search-backdrop" aria-label="Fechar busca" onClick={close} className="absolute inset-0 h-full w-full cursor-default bg-black/55 backdrop-blur-md" />
      <motion.div data-testid="search-panel" data-lenis-prevent role="dialog" aria-label="Buscar produtos"
        initial={{ y: -40 }} animate={{ y: 0 }} exit={{ y: -40 }} transition={{ duration: 0.45, ease: EASE }}
        className="relative max-h-[88vh] overflow-y-auto border-b border-white/10 bg-[#0b0b0d]/95 backdrop-blur-xl">
        <div className="mx-auto max-w-[760px] px-5 pb-10 pt-5 sm:px-8 sm:pt-8">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <Search className="h-5 w-5 shrink-0 text-zinc-400 sm:h-6 sm:w-6" />
            <input ref={input} data-testid="search-input" value={query} onChange={(e) => setQuery(e.target.value)} type="text" inputMode="search" enterKeyHint="search" autoComplete="off"
              onKeyDown={(e) => e.key === "Enter" && q && !list.length && order(q)}
              placeholder="Buscar na RC iPhones" className="min-w-0 flex-1 bg-transparent text-xl font-semibold text-white placeholder:text-zinc-500 outline-none sm:text-3xl" />
            <button data-testid="search-close-button" onClick={close} aria-label="Fechar busca" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-zinc-300 transition-colors hover:bg-white/10 hover:text-white">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="mt-5">
            {!q ? <SearchShortcuts items={shortcuts} />
              : list.length ? <SearchResults list={list} query={q} onPick={close} onOrder={() => order(q)} />
                : <SearchEmpty query={q} onOrder={() => order(q)} onAlert={() => report("alert")} />}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const SearchButton = ({ go }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="header-search-button" onClick={() => setOpen(true)} aria-label="Buscar produtos" title="Buscar"
        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:bg-white/10">
        <Search className="h-4 w-4" />
      </button>
      {createPortal(<AnimatePresence>{open && <Panel onClose={() => setOpen(false)} go={go} />}</AnimatePresence>, document.body)}
    </>
  );
};
